/**
 * Which qFlow runs a live dataset pulls: the workspace (first: nothing else
 * can be listed without it), the workflow (by id: runs carry no name) and its version, the run status, real or test runs, an optional
 * random sample, and node-output rules ("node triage's branch is urgent").
 * The period is not here: each experiment picks it when it runs.
 */

import { useEffect, useId, useState } from "react";
import { Plus, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { errorText } from "@/components/ui/states";
import { SourceAuthError } from "@/features/settings/reconnect";
import { FilterButton } from "./filter-button";
import { useTraceFilters } from "./hooks";
import {
  STATUSES,
  isFieldPath,
  nodeRules,
  runKind,
  withColumn,
  withNodeRules,
  withRunKind,
  type NodeRule,
  type RunKind,
} from "./qflow-query";
import { isAuthError } from "./sources";
import type { TraceQuery } from "./trace-query";
import { WorkspacePicker, qflowWorkspace } from "./qflow-workspace";

const KINDS: Array<[RunKind, string]> = [
  ["false", "Real runs"],
  ["true", "Test runs"],
  ["any", "Both"],
];
const BLANK: NodeRule = { node: "", field: "", value: "" };
const WHOLE = /^[1-9]\d*$/;
/** What is wrong with a rule; any problem holds back Save until fixed or removed. */
const ruleProblem = (r: NodeRule) => {
  if (r.node.includes(".")) return "A node id has no dots. Put a nested path in Field, like result.label.";
  if (![r.node, r.field, r.value].every((x) => x.trim()))
    return "Fill in node id, field and value, or remove the rule.";
  if (!isFieldPath(r.field))
    return "Field is a dot path like result.label: no dot at either end, none doubled.";
  return null;
};

const Label = ({ id, htmlFor, children }: { id?: string; htmlFor?: string; children: string }) =>
  htmlFor ? (
    <label htmlFor={htmlFor} className="text-xs font-semibold">
      {children}
    </label>
  ) : (
    <span id={id} className="text-xs font-semibold">
      {children}
    </span>
  );

export function QflowFilters({
  sourceId,
  value,
  onChange,
  onBlocked,
}: {
  sourceId: string;
  value: TraceQuery;
  onChange: (next: TraceQuery) => void;
  /** Why the filters cannot be saved as typed, or null; Save waits on it. */
  onBlocked?: ((why: string | null) => void) | undefined;
}) {
  const id = useId();
  const facets = useTraceFilters(sourceId, value);
  const workflows = facets.data?.trace_names ?? [];
  const found = new Set(workflows.map((w) => w.value));
  const choices = [
    ...workflows.map((w) => ({ value: w.value, label: w.value, count: w.count })),
    ...value.traceNames.filter((v) => !found.has(v)).map((v) => ({ value: v, label: v, count: 0 })),
  ];
  // Half-typed rules stay here; only complete ones reach the filters.
  const [rules, setRules] = useState<NodeRule[]>(() => nodeRules(value));
  const editRules = (next: NodeRule[]) => (setRules(next), onChange(withNodeRules(value, next)));
  // What is typed stays as typed; only a whole number reaches the filters (a wrong one clears it and says why).
  const [version, setVersion] = useState(value.version);
  const [sample, setSample] = useState(value.columns.sample?.[0] ?? "");
  const badVersion = version !== "" && !WHOLE.test(version);
  const badSample = sample !== "" && !WHOLE.test(sample);
  const problems = rules.map(ruleProblem);
  const picked = qflowWorkspace(value);
  const blocked = !picked
    ? "Pick a qFlow workspace first."
    : badVersion
      ? "Fix the version first."
      : badSample
        ? "Fix the sample size first."
        : problems.some(Boolean)
          ? "Finish or remove the marked node rule first."
          : null;
  useEffect(() => {
    onBlocked?.(blocked);
    return () => onBlocked?.(null);
  }, [blocked, onBlocked]);
  const kind = runKind(value);
  const pickKind = (k: RunKind) => onChange(withRunKind(value, k));
  // Arrow keys move through the run kinds, as in any radio group.
  const arrowKind = (e: React.KeyboardEvent, i: number) => {
    const step =
      e.key === "ArrowRight" || e.key === "ArrowDown"
        ? 1
        : e.key === "ArrowLeft" || e.key === "ArrowUp"
          ? -1
          : 0;
    if (!step) return;
    e.preventDefault();
    const next = (i + step + KINDS.length) % KINDS.length;
    pickKind(KINDS[next]![0]);
    (e.currentTarget.parentElement?.children[next] as HTMLElement | undefined)?.focus();
  };
  const status = value.columns.status ?? [];

  return (
    <div className="flex flex-col gap-3">
      {facets.isError &&
        (isAuthError(facets.error) ? (
          <SourceAuthError connectionId={sourceId} label="qFlow" />
        ) : (
          <span role="alert" className="text-xs text-err">
            {errorText(facets.error, "qFlow could not use this filter.")}
          </span>
        ))}
      <div className="flex flex-col gap-1">
        <Label>Workspace</Label>
        <span className="flex min-h-[34px] items-center gap-2">
          <WorkspacePicker sourceId={sourceId} value={value} onChange={onChange} />
          {!picked && <span className="text-xs text-muted">The qFlow workspace whose runs are read.</span>}
        </span>
      </div>
      {picked && (
        <>
          <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
            <div className="flex flex-col gap-1">
              <Label>Workflow</Label>
              <span className="flex min-h-[34px] items-center">
                <FilterButton
                  label="Workflow"
                  search
                  choices={choices}
                  chosen={value.traceNames}
                  onChange={(traceNames) => onChange({ ...value, traceNames })}
                />
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-version-input`}>Version</Label>
              <input
                id={`${id}-version-input`}
                inputMode="numeric"
                className="inp w-24 font-mono"
                placeholder="Any"
                aria-invalid={badVersion}
                aria-describedby={badVersion ? `${id}-version` : undefined}
                value={version}
                onChange={(e) => {
                  const v = e.target.value.trim();
                  setVersion(v);
                  onChange({ ...value, version: WHOLE.test(v) ? v : "" });
                }}
              />
              {badVersion && (
                <span id={`${id}-version`} className="max-w-24 text-xs text-err">
                  A whole number, like 3
                </span>
              )}
            </div>
            <div className="flex flex-col gap-1">
              <Label>Status</Label>
              <span className="flex min-h-[34px] items-center">
                <FilterButton
                  label="Status"
                  choices={STATUSES.map((s) => ({ value: s, label: s.replace("_", " ") }))}
                  chosen={status}
                  onChange={(next) => onChange(withColumn(value, "status", next))}
                />
              </span>
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={`${id}-sample-input`}>Random sample</Label>
              <input
                id={`${id}-sample-input`}
                inputMode="numeric"
                className="inp w-28 font-mono"
                placeholder="All runs"
                aria-invalid={badSample}
                aria-describedby={badSample ? `${id}-sample` : undefined}
                value={sample}
                onChange={(e) => {
                  const n = e.target.value.trim();
                  setSample(n);
                  onChange(withColumn(value, "sample", WHOLE.test(n) ? [n] : []));
                }}
              />
              {badSample && (
                <span id={`${id}-sample`} className="max-w-28 text-xs text-err">
                  1 or more
                </span>
              )}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <Label id={`${id}-runs`}>Runs</Label>
            <div role="radiogroup" aria-labelledby={`${id}-runs`} className="flex flex-wrap gap-1.5">
              {KINDS.map(([k, label], i) => (
                <button
                  key={k}
                  type="button"
                  role="radio"
                  aria-checked={kind === k}
                  tabIndex={kind === k ? 0 : -1}
                  onClick={() => pickKind(k)}
                  onKeyDown={(e) => arrowKind(e, i)}
                  className={`h-7 rounded-full border px-2.5 text-xs font-medium transition-colors ${
                    kind === k
                      ? "border-primary bg-primary text-on-primary"
                      : "border-input text-body hover:bg-subtle"
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>
          <fieldset className="flex flex-col gap-1.5">
            <legend className="mb-1 text-xs font-semibold">Node output rules</legend>
            <span className="text-xs text-muted">
              Keep a run only when a node&rsquo;s output field has this value. A condition node&rsquo;s chosen
              path is its <code className="font-mono">branch</code> field.
            </span>
            {rules.map((r, i) => (
              <div key={i} className="flex flex-col gap-1">
                <div
                  className="flex max-w-2xl flex-wrap items-center gap-1.5 sm:flex-nowrap"
                  role="group"
                  aria-label={`Rule ${i + 1}`}
                  aria-describedby={problems[i] ? `${id}-rule-${i}` : undefined}
                >
                  {(["node", "field", "value"] as const).map((part) => (
                    <input
                      key={part}
                      aria-invalid={
                        !!problems[i] &&
                        (!r[part].trim() ||
                          (part === "node" && r.node.includes(".")) ||
                          (part === "field" && !isFieldPath(r.field)))
                      }
                      aria-label={part === "node" ? "Node id" : part === "field" ? "Field" : "Value"}
                      className="inp min-w-0 flex-1 basis-28 font-mono text-xs"
                      placeholder={
                        part === "node"
                          ? "Node id"
                          : part === "field"
                            ? "e.g. branch or result.label"
                            : "Value"
                      }
                      value={r[part]}
                      onChange={(e) =>
                        editRules(rules.map((x, j) => (j === i ? { ...x, [part]: e.target.value } : x)))
                      }
                    />
                  ))}
                  <button
                    type="button"
                    aria-label={`Remove rule ${i + 1}`}
                    className="grid size-9 shrink-0 place-items-center rounded-md text-muted hover:bg-subtle hover:text-ink"
                    onClick={() => editRules(rules.filter((_, j) => j !== i))}
                  >
                    <X size={14} aria-hidden />
                  </button>
                </div>
                {problems[i] && (
                  <span id={`${id}-rule-${i}`} className="text-xs text-err">
                    {problems[i]}
                  </span>
                )}
              </div>
            ))}
            <span>
              <Button size="sm" onClick={() => setRules([...rules, BLANK])}>
                <Plus size={13} strokeWidth={2.2} aria-hidden />
                Add rule
              </Button>
            </span>
          </fieldset>
        </>
      )}
    </div>
  );
}
